import { Fragment, useRef, useState, useContext } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import AuthContext from "../AuthContext";
import { apiFetch } from "../api/client";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";

export default function AddVendor() {
    const authContext = useContext(AuthContext);
    const notify = useNotification();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState({
        vendorName: "",
        phoneNumber: "",
    });

    const handleInputChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const [open, setOpen] = useState(true);
    const cancelButtonRef = useRef(null);

    const addVendor = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            if (!form.vendorName || !form.vendorName.trim()) {
                notify("error", { title: "Validation", text: "Vendor name is required." });
                return;
            }

            // Backend expects VendorRequestDto { VendorName, PhoneNumber }
            const payload = {
                VendorName: form.vendorName || form.name,
                PhoneNumber: form.phoneNumber,
            };

            const res = await apiFetch(`/api/vendor`, {
                method: "POST",
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const parsed = await res.json().catch(() => null);
                const note = mapApiErrorToNotification(parsed, res.status);
                notify("error", note);
                return;
            }

            notify("success", {
                title: "Vendor Added",
                text: "Vendor added successfully.",
            });
            setOpen(false);
        } catch (err) {
            console.error(err);
            notify("error", {
                title: "Network error",
                text: "Could not reach server.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-10"
                initialFocus={cancelButtonRef}
                onClose={setOpen}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <PlusIcon
                                                className="h-6 w-6 text-blue-400"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left ">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-lg font-semibold leading-6 text-gray-900"
                                            >
                                                Vendor Information
                                            </Dialog.Title>

                                            <form className="mt-4">
                                                <div className="grid gap-4 mb-4">
                                                    <div>
                                                        <label
                                                            htmlFor="vendorName"
                                                            className="block mb-2 text-sm font-medium text-gray-900"
                                                        >
                                                            Vendor Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="vendorName"
                                                            value={form.vendorName}
                                                            onChange={handleInputChange}
                                                            className="w-full border border-gray-300 rounded-lg p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            placeholder="Enter vendor name"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label
                                                            htmlFor="phoneNumber"
                                                            className="block mb-2 text-sm font-medium text-gray-900"
                                                        >
                                                            Phone Number
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="phoneNumber"
                                                            value={form.phoneNumber}
                                                            onChange={handleInputChange}
                                                            className="w-full border border-gray-300 rounded-lg p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            placeholder="Enter phone number"
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        onClick={addVendor}
                                        disabled={isSubmitting}
                                        aria-busy={isSubmitting}
                                        className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                                    >
                                        {isSubmitting && (
                                            <svg className="animate-spin -ml-2 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                            </svg>
                                        )}
                                        {isSubmitting ? "Adding..." : "Add Vendor"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setOpen(false)}
                                        ref={cancelButtonRef}
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
